import { useEffect, useState } from "react";
import type { ReceivedMessagePublic, ResponseOption } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, CheckCircle2 } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

const RESPONSE_COLOR_CLASSES: Record<string, string> = {
  green: "border-green-500 text-green-600 bg-green-50",
  red: "border-red-500 text-red-600 bg-red-50",
  yellow: "border-yellow-500 text-yellow-600 bg-yellow-50",
};

function ResponseButtons({
  options,
  currentValue,
  onRespond,
}: {
  options: ResponseOption[];
  currentValue: string | null;
  onRespond: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {options.map((opt) => {
        const isSelected = currentValue === opt.value;
        const colorClass = opt.color ? RESPONSE_COLOR_CLASSES[opt.color] ?? "" : "";
        return (
          <Button
            key={opt.value}
            variant={isSelected ? "default" : "outline"}
            size="sm"
            className={isSelected ? colorClass : ""}
            onClick={(e) => { e.stopPropagation(); onRespond(opt.value); }}
          >
            {isSelected && <CheckCircle2 className="mr-1 h-3 w-3" />}
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

export default function MessagesPage() {
  const [messages, setMessages] = useState<ReceivedMessagePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReceivedMessagePublic | null>(null);
  const [pinned, setPinned] = useState(false);

  async function loadMessages() {
    setLoading(true);
    try {
      const res = await api.getMyMessages();
      setMessages(res.messages);
      setError(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMessages();
    // Poll every 30 seconds
    const interval = setInterval(loadMessages, 30_000);
    return () => clearInterval(interval);
  }, []);

  async function handleRespond(messageId: string, value: string) {
    try {
      await api.respondToMessage(messageId, value);
      await loadMessages();
    } catch {
      // ignore
    }
  }

  async function handleSelect(msg: ReceivedMessagePublic) {
    setSelected(msg);
    // Mark as read (fire-and-forget)
    api.markMessageRead(msg.id).catch(() => {});
  }

  if (loading && messages.length === 0) {
    return (
      <div className="flex flex-col gap-6 p-6" data-page="messages-list" data-state="loading">
        <h1 className="text-2xl font-semibold">Messages</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full gap-0" data-page="messages-list" data-state={error ? "error" : messages.length === 0 ? "empty" : "ready"}>
    <div className="min-w-0 flex-1 flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <MessageSquare className="h-6 w-6" />
          Messages
        </h1>
        <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned(p => !p)} />
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {messages.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6 flex flex-col items-center gap-3 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-muted-foreground">No messages yet</p>
            <p className="text-sm text-muted-foreground">
              Messages from coaches and administrators will appear here.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3" data-region="results">
        {messages.map((msg) => (
          <Card
            key={msg.id}
            className={`cursor-pointer transition-colors ${selected?.id === msg.id ? "border-primary" : ""}`}
            onClick={() => handleSelect(msg)}
            data-item="message"
            data-item-id={msg.id}
          >
            <CardContent className="pt-4 pb-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{msg.sender_name}</span>
                    {/* 163 -- event-info vs team stream */}
                    {msg.category === "event_info" && (
                      <span className="inline-flex items-center rounded-full bg-[#E6F1FB] px-2 py-0.5 text-xs font-medium text-[#0C447C]" data-category="event_info">Event info</span>
                    )}
                    {msg.category === "team" && (
                      <span className="inline-flex items-center rounded-full bg-[#E1F5EE] px-2 py-0.5 text-xs font-medium text-[#085041]" data-category="team">
                        Team{msg.subgroup ? ` - ${msg.subgroup}` : ""}
                      </span>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {msg.context_label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatTime(msg.sent_at)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm line-clamp-2">{msg.content}</p>
                </div>
              </div>

              {/* Response buttons */}
              {msg.response_options && msg.response_options.length > 0 && (
                <div className="mt-1">
                  <ResponseButtons
                    options={msg.response_options}
                    currentValue={msg.my_latest_response}
                    onRespond={(value) => handleRespond(msg.id, value)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
    <PreviewPane pinned={pinned} onTogglePin={() => setPinned(p => !p)} title="Message">
      {selected && (
        <>
          <PreviewField label="From">{selected.sender_name}</PreviewField>
          <PreviewField label="Context">{selected.context_label}</PreviewField>
          <PreviewField label="Time">{formatTimeFull(selected.sent_at)}</PreviewField>
          <PreviewField label="Message">
            <span className="whitespace-pre-wrap">{selected.content}</span>
          </PreviewField>
          {selected.response_options && selected.response_options.length > 0 && (
            <PreviewField label="Your Response">
              <ResponseButtons
                options={selected.response_options}
                currentValue={selected.my_latest_response}
                onRespond={(value) => handleRespond(selected.id, value)}
              />
            </PreviewField>
          )}
        </>
      )}
    </PreviewPane>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  // Today: show time
  if (diff < 24 * 60 * 60 * 1000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  // This week: show day + time
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString([], { weekday: "short" }) +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  // Older: show date
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

function formatTimeFull(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
